import { useLingui } from '@lingui/react/macro';
import { ArrangeHorizontal } from 'iconsax-reactjs';
import { type FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCalculatePrice } from '@/account/trade/convert/api';
import { CoinPriceInput } from '@/account/trade/convert/CoinPriceInput';
import { useCoins } from '@/shared/api';
import { Button } from '@/shared/Button';
import { cn } from '@/shared/react';

export const UnitConverter: FC<{
  fromCoin?: string;
  toCoin?: string;
  className?: string;
}> = ({ fromCoin = 'BTC', toCoin = 'IQD', className }) => {
  const { t } = useLingui();
  const coins = useCoins();

  const { control, watch, setValue } = useForm({
    defaultValues: {
      from_coin: fromCoin.toUpperCase(),
      to_coin: toCoin.toUpperCase(),
      from_amount: '1',
      to_amount: '0',
    },
  });

  const [lastTouch, setLastTouch] = useState<'from' | 'to'>('from');
  const values = watch();

  const serverPrices = useCalculatePrice(
    values.from_coin,
    values.to_coin,
    lastTouch === 'from' ? values.from_amount : values.to_amount,
    lastTouch,
  );

  /* Reset the widget when the page navigates to a different pair */
  useEffect(() => {
    setValue('from_coin', fromCoin.toUpperCase());
    setValue('to_coin', toCoin.toUpperCase());
    setValue('from_amount', '1');
    setLastTouch('from');
  }, [fromCoin, toCoin, setValue]);

  /* Revalidate the other input from the server once the debounced amount resolves */
  useEffect(() => {
    if (lastTouch === 'from' && serverPrices.data?.to_amount) {
      setValue('to_amount', serverPrices.data.to_amount);
    }
    if (lastTouch === 'to' && serverPrices.data?.from_amount) {
      setValue('from_amount', serverPrices.data.from_amount);
    }
  }, [
    lastTouch,
    serverPrices.data?.from_amount,
    serverPrices.data?.to_amount,
    setValue,
  ]);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-col gap-4 desktop:flex-row desktop:items-start justify-center">
        <Controller
          control={control}
          name="from_amount"
          render={({ field }) => (
            <CoinPriceInput
              label={t`You Convert`}
              coin={values.from_coin}
              coins={coins.data}
              price={field.value}
              onPriceChange={(n) => {
                setLastTouch('from');
                field.onChange(n ?? '0');
              }}
              onCoinChange={(n) => setValue('from_coin', n)}
              loading={
                (lastTouch === 'to' && serverPrices.isLoading) || !lastTouch
              }
              className="grow"
            />
          )}
        />
        <Button
          fab
          size={10}
          variant="outline"
          className="self-center shrink-0 rotate-90 desktop:rotate-0"
          onClick={() => {
            const nextFromCoin = values.to_coin;
            const nextToCoin = values.from_coin;
            const nextFromAmount = values.to_amount;
            const nextToAmount = values.from_amount;
            setValue('from_coin', nextFromCoin);
            setValue('to_coin', nextToCoin);
            setValue('from_amount', nextFromAmount);
            setValue('to_amount', nextToAmount);
            setLastTouch(lastTouch === 'from' ? 'to' : 'from');
          }}
          aria-label={t`Swap`}
        >
          <ArrangeHorizontal />
        </Button>
        <Controller
          control={control}
          name="to_amount"
          render={({ field }) => (
            <CoinPriceInput
              label={t`You Get`}
              coin={values.to_coin}
              coins={coins.data}
              price={field.value}
              onPriceChange={(n) => {
                setLastTouch('to');
                field.onChange(n ?? '0');
              }}
              onCoinChange={(n) => setValue('to_coin', n)}
              loading={
                (lastTouch === 'from' && serverPrices.isLoading) || !lastTouch
              }
              className="grow"
            />
          )}
        />
      </div>
    </div>
  );
};
